<template>
  <v-row class="mt-0">
    <v-col cols="12" class="text-subtitle-2 text-medium-emphasis text-start font-weight-bold pb-0">
      <span class="text-black">Products</span>
    </v-col>

    <v-col cols="12" class="pt-0">
      <v-slide-group class="slide-group-no-arrows mt-2">
        <v-slide-group-item
          v-for="(src, index) in selectedProducts.map((p: any) => p.productUrl)"
          :key="index"
        >
          <v-card variant="outlined" class="mr-3 rounded-lg" height="72" width="72">
            <v-img :src="src" contain crossorigin="anonymous" height="100%" />
          </v-card>
        </v-slide-group-item>

        <v-slide-group-item>
          <v-btn
            variant="outlined"
            color="primary"
            class="text-capitalize"
            prepend-icon="mdi-pencil"
            @click.stop="onChangeProducts"
            :disabled="editorStore.isShufflingPlacement || canvasStore.canvasLoader"
          >
            Change Products
          </v-btn>
        </v-slide-group-item>
      </v-slide-group>
    </v-col>
  </v-row>

  <v-dialog v-model="showDialog" max-width="820" :persistent="editorStore.isRegenerating">
    <v-card elevation="8">
      <v-card-title class="text-h6 pa-6 pb-0">Change Products</v-card-title>
      <v-card-text class="pa-6 pt-4">
        <div class="info-banner mb-6 pa-4 rounded-lg d-flex align-center">
          <v-icon color="primary" class="mr-3">mdi-information</v-icon>
          <span class="text-body-1 text-primary"
            >Updating the products will automatically generate fresh creatives, replacing the
            current ones.</span
          >
        </div>

        <div class="d-flex align-center justify-space-between mb-4">
          <span class="text-subtitle-1 text-medium-emphasis">Select up to 3 products</span>
          <v-menu
            location="right top"
            :close-on-content-click="false"
            width="360"
            v-model="productMenu"
            @update:modelValue="onMenuChange"
          >
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                variant="outlined"
                color="primary"
                prepend-icon="mdi-plus"
                class="text-capitalize"
                :disabled="selectedProducts.length >= 3 || editorStore.isRegenerating"
              >
                Add
              </v-btn>
            </template>
            <v-card>
              <v-card-title class="pt-4">
                <v-text-field
                  v-model="productSearch"
                  placeholder="Search products"
                  prepend-inner-icon="mdi-magnify"
                  bg-color="grey-lighten-3"
                  variant="solo"
                  density="compact"
                  class="border-0"
                  clearable
                  flat
                  hide-details
                  :disabled="editorStore.isRegenerating"
                ></v-text-field>
              </v-card-title>
              <v-card-text class="products-list pr-3">
                <v-expansion-panels v-model="categoryPanel" flat multiple>
                  <v-expansion-panel
                    v-for="(category, index) in productCategory"
                    :key="category"
                    bg-color="grey-lighten-3"
                    class="pa-0"
                    :value="index"
                    static
                  >
                    <v-expansion-panel-title class="px-4 font-weight-medium">
                      {{ category }}
                    </v-expansion-panel-title>
                    <v-expansion-panel-text class="category-list">
                      <v-list
                        max-height="226"
                        bg-color="grey-lighten-3"
                        class="pt-0"
                        color="primary"
                      >
                        <v-list-item
                          v-for="product in filteredProducts.filter(
                            (product) => product.parentCategory == category,
                          )"
                          :key="product.id"
                          :class="['pa-1', 'mb-1', 'mx-2', {}]"
                          rounded
                          @click="toggleProduct(product)"
                          :active="selectedProducts.find((p) => p.id == product.id) ? true : false"
                          :disabled="
                            (selectedProducts.length >= 3 &&
                              !selectedProducts.find((p) => p.id == product.id)) ||
                            editorStore.isRegenerating
                          "
                          v-slot="{ isActive }"
                        >
                          <v-row>
                            <v-col cols="3">
                              <v-list-item-media class="bg-grey-lighten-2 rounded pa-1">
                                <v-img
                                  :src="product.productUrl"
                                  width="48"
                                  height="40"
                                  crossorigin="anonymous"
                                ></v-img>
                              </v-list-item-media>
                            </v-col>
                            <v-col :cols="isActive ? 7 : 9" class="pl-0">
                              <v-tooltip :open-delay="500" :max-width="400">
                                <template #activator="{ props }">
                                  <v-list-item-title v-bind="props">
                                    {{ product.parentProductName }}
                                  </v-list-item-title>
                                </template>
                                {{ product.parentProductName }}
                              </v-tooltip>
                              <v-list-item-subtitle class="product_quantitiy">
                                {{ product.weightInGrams + product.unitOfMeasure }}
                              </v-list-item-subtitle>
                            </v-col>
                            <v-col v-if="isActive" cols="2" align-self="center">
                              <v-icon icon="mdi-check-circle" color="primary"></v-icon>
                            </v-col>
                          </v-row>
                        </v-list-item>
                      </v-list>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-card-text>
            </v-card>
          </v-menu>
        </div>

        <v-row no-gutters>
          <v-col
            v-for="(p, idx) in selectedProducts"
            :key="(p as any).id ?? idx"
            cols="12"
            md="4"
            class="pa-2"
          >
            <v-card>
              <v-card-text class="pa-1">
                <v-row no-gutters class="flex-nowrap">
                  <v-col cols="auto">
                    <v-img
                      :src="p.productUrl"
                      width="45"
                      crossorigin="anonymous"
                      class="bg-grey-lighten-2 rounded mr-1"
                    ></v-img>
                  </v-col>
                  <v-col class="product-details">
                    <v-tooltip :open-delay="500" :max-width="400">
                      <template #activator="{ props }">
                        <v-list-item-title v-bind="props">
                          {{ productTitle(p) }}
                        </v-list-item-title>
                      </template>
                      <span>{{ productTitle(p) }}</span>
                    </v-tooltip>
                    <v-list-item-subtitle>
                      <v-row>
                        <v-col class="product_quantitiy text-truncate">
                          {{ productSize(p) }}
                        </v-col>
                        <v-col cols="auto">
                          <v-btn
                            variant="text"
                            icon="mdi-close"
                            size="small"
                            density="compact"
                            @click="removeProduct(idx)"
                            :disabled="editorStore.isRegenerating"
                          ></v-btn>
                        </v-col>
                      </v-row>
                    </v-list-item-subtitle>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="pa-6 pt-0">
        <v-btn
          variant="outlined"
          class="px-6"
          @click="closeDialog"
          :disabled="editorStore.isRegenerating"
          >Cancel</v-btn
        >
        <v-btn
          color="primary"
          variant="flat"
          class="px-6"
          :disabled="!canRebuild || editorStore.isRegenerating || selectedProducts.length === 0"
          @click="handleRebuild"
        >
          <v-progress-circular
            v-if="editorStore.isRegenerating"
            indeterminate
            size="20"
            width="2"
            color="white"
            class="mr-2"
          ></v-progress-circular>
          Rebuild Creative
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { usePlacementStore } from '@/stores/placement'
import { useProductStore } from '@/stores/product'
import type { Product } from '@/types/product'
import { useCanvasStore } from '@/stores/canvas'
import { useBrandStore } from '@/stores/brand'
import { useUndoRedoStore } from '@/stores/undoRedo'
import { useEditorStore } from '@/stores/editor'
import { useAppStore } from '@/stores/app'

import EditorService from '@/services/editorService'
import ProductService from '@/services/productService'

const placementStore = usePlacementStore()
const productStore = useProductStore()
const canvasStore = useCanvasStore()
const brandStore = useBrandStore()
const editorStore = useEditorStore()
const appStore = useAppStore()
const undoRedoStore = useUndoRedoStore()
const editorService = new EditorService()
const productService = new ProductService()

const emit = defineEmits(["loadCanvas"])
// Dialog state
const showDialog = ref(false)

// Product selection modal state
const productSearch = ref('')
const productMenu = ref(false)
const categoryPanel = ref([0])

// Product selection logic
const filteredProducts = computed(() => {
  if (productSearch.value) {
    return productStore.products.filter((product) =>
      product.parentProductName.toLowerCase().includes(productSearch.value.toLowerCase()),
    )
  }
  return productStore.products
})

const productCategory = computed(() => {
  return Array.from(
    new Map(filteredProducts.value.map((product) => [product.parentCategory, product])).keys(),
  )
})

const toggleProduct = (product: Product) => {
  if (selectedProducts.value.find((p) => p.id == product.id)) {
    // Product is already selected, remove it
    const updatedProducts = selectedProducts.value.filter((p) => p.id != product.id)
    editorStore.setActiveProducts(updatedProducts)
    editorStore.activeProductDataItems = updatedProducts.map((product: any) => product.productInfo)
  } else {
    // Product is not selected, add it
    const updatedProducts = [...selectedProducts.value, product]
    editorStore.setActiveProducts(updatedProducts)
    editorStore.activeProductDataItems = updatedProducts.map((product: any) => product.productInfo)
  }
}

const onChangeProducts = () => {
  showDialog.value = true
}

// Selected products from editor store
const selectedProducts = computed<Product[]>(() => editorStore.activeProducts)

// Enable rebuild only when selection differs from base
const canRebuild = computed(() => {
  const base = (productStore.baseProducts || []).map((p: any) => p.id).join(',')
  const selected = selectedProducts.value.map((p: any) => p.id).join(',')
  return base !== selected
})

const productTitle = (p: Product) => p.parentProductName || 'Product'

const productSize = (p: Product) => {
  const weight = p.weightInGrams || ''
  const uom = p.unitOfMeasure || ''
  if (weight && uom) return `${weight}${uom}`
  return ''
}

const removeProduct = (index: number) => {
  const updatedProducts = [...selectedProducts.value]
  updatedProducts.splice(index, 1)
  editorStore.setActiveProducts(updatedProducts)
  editorStore.activeProductDataItems = updatedProducts.map((product: any) => product.productInfo)
}

const closeDialog = () => {
  // Reset active products to base products when canceling
  if (productStore.baseProducts && productStore.baseProducts.length > 0) {
    editorStore.setActiveProducts([...productStore.baseProducts])
    editorStore.activeProductDataItems = productStore.baseProducts.map(
      (product: any) => product.productInfo,
    )
  }
  showDialog.value = false
}

const onMenuChange = (val: boolean) => {
  if (!val) {
    setTimeout(() => {
      productSearch.value = "";
    }, 250);
  }
};

const handleRebuild = () => {
  // set the new product count
  editorService.generateCreative().then((success) => {
    if (success) {
      editorStore.setProductCount(selectedProducts.value.length)
      emit("loadCanvas")
      showDialog.value = false
      undoRedoStore.clearHistory()
    }
  })
}

onMounted(() => {
  if (productStore.baseProducts && productStore.baseProducts.length > 0) {
    editorStore.setActiveProducts([...productStore.baseProducts])
    editorStore.activeProductDataItems = productStore.baseProducts.map(
      (product: any) => {
        const productInfo = product.productInfo
        productInfo.productData.isMultiple = product.metaData ? JSON.parse(product.metaData)['isMultiple'] : false
        return productInfo
      },
    )
  }

  // Load products if not already loaded
  if (productStore.products.length === 0 && brandStore.selectedBrand) {
    loadProducts()
  }
})

const loadProducts = async () => {
  try {
    if (brandStore.selectedBrand) {
      const products = await productService.getProducts(
        appStore.selectedBusinessProfileId,
        brandStore.selectedBrand.name,
      )
      productStore.setProducts(products)
    }
  } catch (error) {
    console.error('Failed to load products:', error)
  }
}
</script>

<style scoped>
:deep(.slide-group-no-arrows) {
  .v-slide-group__prev,
  .v-slide-group__next {
    display: none !important;
  }
}

:deep(.v-slide-group__container) {
  overflow-x: auto !important;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

:deep(.v-slide-group__container)::-webkit-scrollbar {
  display: none;
}

:deep(.v-slide-group__content) {
  padding-bottom: 8px !important;
  display: flex;
  align-items: center;
}

.product-pill {
  border-radius: 12px;
}

.info-banner {
  background-color: rgba(var(--v-theme-primary), 0.1);
  border: 1px solid rgba(var(--v-theme-primary), 0.2);
}

.product-card {
  border-radius: 8px;
  transition: all 0.2s ease;
}

.product-card:hover {
  border-color: rgba(var(--v-theme-primary), 0.5);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.product-details {
  width: 100px;
}

.product_quantitiy {
  line-height: 1.1rem;
}

.products-list {
  max-height: 290px;
  overflow: scroll;
}

.category-list {
  max-height: 226px;
  overflow: auto;
}

:deep(.category-list .v-expansion-panel-text__wrapper) {
  padding: 0 !important;
}
</style>
